import { InfoCircleFilled } from '@ant-design/icons';
import { Spin, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { RowSelectMethod, TableRowSelection } from 'antd/lib/table/interface';
import React, { useContext, useEffect, useState } from 'react';
import { batch } from 'react-redux';
import { ScrollType, buildColumn, handleResponse } from '../../../../../Helper';
import { deviceAdminApi } from '../../../../../api';
import { NavigationListItem } from '../../../../../model/CommonModel';
import {
	ApplicationPermission,
	ApplicationSubPermission,
	ApplicationSubPermissionType,
	RoleApplicationPermissionContext,
	RoleTab,
} from '../../../../../model/DeviceAdminModel';
import { SearchColumn } from '../../../../common';
import { NavigationList } from '../../../../common/NavigationList/NavigationList';
import { CloneFromRole } from '../CloneFromRole/CloneFromRole';
import { setAppPermissionsAction } from '../RoleContext/action';
import { RoleStore } from '../RoleContext/context';
import styles from './applicationPermissions.module.scss';

type Props = {};

//Avoid creating object style inline, since increases reconciliations
const scroll: ScrollType = { y: 459 };

const ApplicationPermissions: React.FC<Props> = () => {
	//#region state management variables
	const {
		roleContextInitialState: {
			props: { roleId },
			applicationPermissions: { applicationPermissions },
		},
		dispatcher,
	} = useContext(RoleStore);

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isTableLoading, setIsTableLoading] = useState<boolean>(true);
	const [selectedComponentId, setSelectedComponentId] = useState<number>(0);
	const [resetSearchColumn, setResetSearchColumn] = useState<boolean>(false);
	//#endregion

	useEffect(() => {
		if (roleId) {
			deviceAdminApi
				.getApplicationPermissions(roleId)
				.then(res => {
					if (!handleResponse(res)) {
						const dataSource: ApplicationPermission[] = res.Entity;
						if (dataSource.length) {
							batch(() => {
								setSelectedComponentId(dataSource[0].ComponentId);
								dataSource.forEach((x: ApplicationPermission) =>
									x.ApplicationSubPermissions.forEach((y: ApplicationSubPermission) => {
										y.key = y.SubComponentId;
									})
								);
								dispatcher(setAppPermissionsAction({ applicationPermissions: dataSource }));
							});
						}
					}
				})
				.finally(() => {
					batch(() => {
						setIsLoading(false);
						setIsTableLoading(false);
					});
				});
		}
	}, [roleId]);

	const selectedRowKeys: React.Key[] = [];
	const selectedApplicationPermission: ApplicationPermission = applicationPermissions.find(x => x.ComponentId === selectedComponentId);
	const permissions: ApplicationSubPermission[] = selectedApplicationPermission?.ApplicationSubPermissions ?? [];
	permissions.forEach(p => {
		if (p.IsChecked) {
			selectedRowKeys.push(p.SubComponentId);
		}
	});

	const handleOnSearch = () => {
		setResetSearchColumn(false);
	};

	const columns: ColumnsType<ApplicationSubPermission> = [
		{
			...buildColumn(
				selectedApplicationPermission ? `${_('Permissions')} for ${selectedApplicationPermission.Name}` : _('Permissions'),
				'Name',
				'auto',
				'start'
			),
			key: '1',
			sorter: (a, b) => a.Name.localeCompare(b.Name),
			...SearchColumn({
				maxLength: 20,
				dataIndex: 'Name',
				reset: undefined,
				label: undefined,
				notVisible: undefined,
				resetSearch: resetSearchColumn,
				clearSelection: undefined,
				handleResetSearch: () => setResetSearchColumn(false),
				setSearchResults: (_: string) => handleOnSearch(),
				setSearchedValue: undefined,
				searchColumnId: undefined,
				setSearchPerformed: undefined,
			}),
		},
	];

	const handlePermissionsSelected = (selectedRows: ApplicationSubPermission[], type: RowSelectMethod) => {
		const removedItem = selectedRows.length < selectedRowKeys.length;

		let addAdded = false;
		let viewAdded = false;
		let wholeSubPermissionsRemoved = false;
		if (removedItem) {
			wholeSubPermissionsRemoved = !selectedRows.some(x => x.PermissionType === ApplicationSubPermissionType.SubPermissionType);
		} else {
			addAdded =
				!selectedRowKeys.some(x => x.toString().startsWith(`${ApplicationSubPermissionType.AddNewType}-`)) &&
				selectedRows.some(x => x.PermissionType === ApplicationSubPermissionType.AddNewType);

			viewAdded =
				!selectedRowKeys.some(x => x.toString().startsWith(`${ApplicationSubPermissionType.ViewType}-`)) &&
				selectedRows.some(x => x.PermissionType === ApplicationSubPermissionType.ViewType);
		}

		const indexOf = applicationPermissions.findIndex(x => x.ComponentId === selectedComponentId);
		wholeSubPermissionsRemoved = applicationPermissions[indexOf].ApplicationSubPermissions.some(
			sub => sub.PermissionType === ApplicationSubPermissionType.SubPermissionType
		)
			? wholeSubPermissionsRemoved
			: false;
		if (indexOf >= 0 && type !== 'all') {
			applicationPermissions[indexOf].ApplicationSubPermissions = applicationPermissions[indexOf].ApplicationSubPermissions.map(sub => {
				let retValue: ApplicationSubPermission = undefined;

				if (removedItem) {
					// removed all sub permission and view permission
					if (
						wholeSubPermissionsRemoved &&
						(sub.PermissionType === ApplicationSubPermissionType.ViewType || sub.PermissionType === ApplicationSubPermissionType.AddNewType)
					) {
						retValue = { ...sub, IsChecked: false };
					}
					if (!selectedRows.some(item => item.SubComponentId === sub.SubComponentId)) {
						retValue = { ...sub, IsChecked: false };
					}
				} else {
					if (addAdded && sub.PermissionType !== ApplicationSubPermissionType.DeleteType) {
						retValue = { ...sub, IsChecked: true };
					}
					if (
						viewAdded &&
						(sub.PermissionType === ApplicationSubPermissionType.ViewType || sub.PermissionType === ApplicationSubPermissionType.SubPermissionType)
					) {
						retValue = { ...sub, IsChecked: true };
					}
					if (!viewAdded && !addAdded) {
						retValue = { ...sub, IsChecked: selectedRows.some(x => x.SubComponentId === sub.SubComponentId) };
					}
				}
				if (!retValue) {
					retValue = sub;
				}

				return retValue;
			});
		} else {
			if (removedItem) {
				clearSelection();
			} else {
				handleSelectAll();
			}
		}

		dispatcher(setAppPermissionsAction({ isDirty: true, applicationPermissions: applicationPermissions }));
	};

	const disableCheckboxRow = (record: ApplicationSubPermission): boolean => {
		if (roleId === 1) {
			return true;
		}

		const addNewSelected = selectedRowKeys.some(x => x.toString().startsWith(`${ApplicationSubPermissionType.AddNewType}-`));
		if (
			addNewSelected &&
			(record.PermissionType === ApplicationSubPermissionType.SaveType || record.PermissionType === ApplicationSubPermissionType.ViewType)
		) {
			return true;
		}

		return false;
	};

	const handleSelectAll = () => changedAllCheckboxes(true);
	const clearSelection = () => changedAllCheckboxes(false);

	const changedAllCheckboxes = (value: boolean) => {
		const indexOf: number = applicationPermissions.findIndex(x => x.ComponentId === selectedComponentId);
		const hasChanged: boolean = applicationPermissions[indexOf].ApplicationSubPermissions.findIndex(x => x.IsChecked !== value) !== -1;
		applicationPermissions[indexOf].ApplicationSubPermissions = applicationPermissions[indexOf].ApplicationSubPermissions.map(sub => {
			return { ...sub, IsChecked: value };
		});

		const appPermissions: RoleApplicationPermissionContext = { applicationPermissions: applicationPermissions };
		if (hasChanged) {
			appPermissions.isDirty = true;
		}
		dispatcher(setAppPermissionsAction(appPermissions));
	};

	const rowSelection: TableRowSelection<ApplicationSubPermission> = {
		preserveSelectedRowKeys: true,
		type: 'checkbox',
		selections:
			roleId === 1
				? undefined
				: [
						{ key: 'SELECTION_ALL_TARGET', text: 'Select all data', onSelect: handleSelectAll },
						{ key: 'SELECTION_NONE', text: 'Clear all data', onSelect: clearSelection },
				  ],
		onChange: (_, selectedRows: ApplicationSubPermission[], info: { type: RowSelectMethod }) => {
			handlePermissionsSelected(selectedRows, info.type);
		},
		getCheckboxProps: (record: ApplicationSubPermission) => ({
			id: `rolesApplicationPermissionTableCheckbox-${record.key}`,
			disabled: disableCheckboxRow(record),
		}),
		selectedRowKeys,
	};

	const dataSource: NavigationListItem[] = applicationPermissions.map(x => {
		return {
			Name: x.Name,
			Id: x.ComponentId as React.Key,
		};
	});

	const getData = (): ApplicationPermission[] => {
		return applicationPermissions;
	};

	const setData = (isDirty: boolean, applicationPermissions: ApplicationPermission[]) => {
		dispatcher(setAppPermissionsAction({ isDirty: isDirty, applicationPermissions: applicationPermissions }));
	};

	return (
		<Spin wrapperClassName={styles.spin} size='large' tip={`${_('Loading')}...`} spinning={isLoading}>
			<CloneFromRole setLoading={setIsLoading} tab={RoleTab.ApplicationPermission} getData={getData} setData={setData} />
			<div className={styles.container}>
				<div className={styles.leftContainer}>
					<NavigationList
						idPrefix='applicationPermission'
						dataSource={dataSource}
						onElementClicked={item => {
							batch(() => {
								setSelectedComponentId(item.Id as number);
								setIsTableLoading(false);
								setResetSearchColumn(true);
							});
						}}
						selectedItemId={selectedComponentId}
						setSelectedItemId={setSelectedComponentId}
					/>
				</div>
				<div className={styles.rightContainer}>
					<Spin wrapperClassName={styles.spinTableContainer} size='large' tip={`${_('Loading')}...`} spinning={isTableLoading}>
						<span className={styles.containerInfo}>
							<InfoCircleFilled className={styles.containerInfoIcon} />
							<p className={styles.containerInfoMessage}>Permissions to Save and View are automatically granted when Add is allowed</p>
						</span>
						<Table
							id={'applicationSubPermissionTable'}
							className={styles.tableStyle}
							pagination={false}
							scroll={scroll}
							size='small'
							dataSource={permissions}
							columns={columns}
							rowSelection={rowSelection}
						/>
					</Spin>
				</div>
			</div>
		</Spin>
	);
};

export { ApplicationPermissions };
